import { Box, Typography } from "@mui/material";
import { Fragment } from "react";

interface SummaryCard {
  summaryTitle: string;
  summaryTitleBgColor: string;
  totalHeight: string;
  box: SummaryCardBoxValues[];
}

export type SummaryCardBoxValues = {
  boldHeading: string | number;
  helperText: string;
  bgColor: string;
  boldColor: string;
};

export type SummaryCardBox = {
  displaySummaryCards: boolean;
  sumaryCardValues: SummaryCardBoxValues[];
};

export default function SummaryCard({
  summaryTitle,
  summaryTitleBgColor,
  totalHeight,
  box,
}: SummaryCard) {
  return (
    <Box
      sx={{
        height: totalHeight,
        width: "fit-content",
        border: "1px solid #DEDEDE",
        borderRadius: "8px",
        margin: "14px 0px 7px 0px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          padding: "15px 20px",
          fontWeight: "500",
          fontSize: "0.9rem",
          bgcolor: summaryTitleBgColor,
          fontStyle: "Poppins",
          letterSpacing: 0,
          borderTopLeftRadius: "8px",
          borderBottomLeftRadius: "8px",
        }}
      >
        {summaryTitle}
      </Typography>
      {box.map((card, index) => {
        return (
          <Fragment key={index}>
            <Box
              sx={{
                bgcolor: card?.bgColor,
                display: "flex",
                margin: "0.9% 10px",
                justifyContent: "center",
                alignItems: "center",
                padding: "2px 10px",
                borderRadius: "4px",
              }}
            >
              {renderBoldHeading(card.boldHeading, card.boldColor)}

              <Typography
                sx={{
                  color: "black",
                  paddingLeft: "10px",
                  fontSize: "12px",
                  letterSpacing: 0,
                }}
              >
                {card?.helperText}
              </Typography>
            </Box>
            {box.length > 1 && index == 0 && (
              <Box
                sx={{
                  borderLeft: "1.7px dashed #DEDEDE",
                  height: "75%",
                  margin: "0 5px",
                }}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}

/**
 * Renders either:
 * 1) a single boldHeading, if not in the pattern of "3 4 5"
 * 2) "3 Cs 4 Other 5 Pc" if exactly three numbers
 */
function renderBoldHeading(boldHeading: string | number, boldColor: string) {
  // Safely convert boldHeading to a string
  const headingStr = boldHeading != null ? String(boldHeading) : "";
  // Split by spaces
  const parts = headingStr.trim().split(" ");

  // Check for exactly three numeric parts
  const isThreeNumbers =
    parts.length === 3 && parts.every((part) => !isNaN(Number(part)));

  if (!isThreeNumbers) {
    // Fallback: just display boldHeading as before
    return (
      <Typography
        sx={{
          color: boldColor,
          fontWeight: "600",
          fontSize: "20px",
          letterSpacing: 0,
        }}
      >
        {headingStr}
      </Typography>
    );
  }

  // If we have "X Y Z" as three numbers -> "X Cs Y Other Z Pc"
  return (
    <Box sx={{ display: "flex", alignItems: "baseline" }}>
      <Typography
        sx={{
          color: boldColor,
          fontWeight: 600,
          fontSize: "20px",
          marginRight: "3px",
        }}
      >
        {parts[0]}
      </Typography>
      <Typography
        sx={{
          color: boldColor,
          fontWeight: 400,    
          fontSize: "14px",
          marginRight: "8px",
        }}
      >
        Cs
      </Typography>

      <Typography
        sx={{
          color: boldColor,
          fontWeight: 600,
          fontSize: "20px",
          marginRight: "3px",
        }}
      >
        {parts[1]}
      </Typography>
      <Typography
        sx={{
          color: boldColor,
          fontWeight: 400,
          fontSize: "14px",
          marginRight: "8px",
        }}
      >
        Other
      </Typography>

      <Typography
        sx={{
          color: boldColor,
          fontWeight: 600,
          fontSize: "20px",
          marginRight: "3px",
        }}
      >
        {parts[2]}
      </Typography>
      <Typography
        sx={{
          color: boldColor,
          fontWeight: 400,
          fontSize: "14px",
        }}
      >
        Pc
      </Typography>
    </Box>
  );
}